import { List, Map, Record } from "immutable";
import { RootActions, RootState } from "common/reducers";
import { receive } from "common/reducers/Utils";
import { AnnotationRecord } from "common/records/Annotation";
import { ElevationRecord, elevationFromJSON } from "common/records/Elevation";
import { annotationFromJSON } from "common/records/Annotation";
import {
  ADD_ANNOTATION,
  AnnotationTarget,
  CLEAR_ELEVATION,
  CLEAR_ELEVATION_SNAPSHOTS,
  MOVE_ANNOTATION,
  RECEIVE_ELEVATION,
  REMOVE_ANNOTATION,
  RESET_ELEVATION,
  RESTORE_ELEVATION,
  SET_MEASURING_METADATA,
  SNAPSHOT_ELEVATION,
  UPDATE_ANNOTATIONS,
  UPDATE_ELEVATION_FIELD,
} from "common/reducers/elevation.actions";

export const ElevationStateRecord = Record({
  byId: Map<number, ElevationRecord>(),
  // Server-saved baseline: written ONLY by server responses (loadMeasurement / createElevation /
  // updateElevation, all via the `receive` helper). This is what `elevationsChanged` and
  // `currentMeasurementChangedElevations` diff against, so the overall Save sees an edit to a
  // saved elevation as dirty and persists it.
  lastSavedById: Map<number, ElevationRecord>(),
  // In-wizard Cancel-rollback checkpoint, mirroring the estimate editor's `editingSnapshotById`
  // (estimateEditor.reducer.ts). Written by each per-step "Save Elevation" (SNAPSHOT_ELEVATION),
  // NOT by `receive` — so a per-step save never moves the server baseline and therefore can't
  // mask a real edit from the overall Save. `RESET_ELEVATION` restores from here.
  editingSnapshotById: Map<number, ElevationRecord>(),
});

export const initialState = ElevationStateRecord();
export type ElevationState = typeof initialState;

const model = "elevations";

export const elevationReducer = (state: RootState, action: RootActions): RootState => {
  if (state && !state.get(model)) {
    state = state.set(model, initialState);
  }

  switch (action.type) {
    case RECEIVE_ELEVATION:
      return receive(state, model, elevationFromJSON(action.payload.elevation));

    case RESTORE_ELEVATION: {
      // Save-failure recovery: puts a captured record back into `byId` WITHOUT touching
      // `lastSavedById` — the elevation must keep reading dirty so the Save button stays enabled
      // and a retry re-sends the restored edits.
      const { elevation } = action.payload;
      return state.setIn([model, "byId", elevation.id], elevation);
    }

    case SNAPSHOT_ELEVATION: {
      // Per-step "Save Elevation" checkpoint for Cancel rollback. Copies the elevation's current
      // `byId` state into `editingSnapshotById` only — deliberately NOT into `lastSavedById`, so a
      // per-step save never clears the server-dirty signal (the overall Save stays enabled until the
      // wizard-level Save persists).
      const { elevationId } = action.payload;
      const elevation = state.getIn([model, "byId", elevationId]) as ElevationRecord | undefined;
      if (!elevation) return state;
      return state.setIn([model, "editingSnapshotById", elevationId], elevation);
    }

    case RESET_ELEVATION: {
      // Roll back to the last in-wizard checkpoint if one exists (Save Elevation → Edit → Cancel
      // restores to that Save), else to the server baseline (edited-but-never-checkpointed →
      // Discard restores to what the server holds), else drop a never-seeded placeholder.
      const { elevationId } = action.payload;
      const snapshot = state.getIn([model, "editingSnapshotById", elevationId]) as ElevationRecord | undefined;
      const lastSaved = state.getIn([model, "lastSavedById", elevationId]) as ElevationRecord | undefined;
      const restored = snapshot ?? lastSaved;
      if (!restored) return state.deleteIn([model, "byId", elevationId]);
      return state.setIn([model, "byId", elevationId], restored);
    }

    case CLEAR_ELEVATION: {
      // Drop a never-saved placeholder from every slice, including its rollback checkpoint.
      const { elevationId } = action.payload;
      return state
        .deleteIn([model, "byId", elevationId])
        .deleteIn([model, "lastSavedById", elevationId])
        .deleteIn([model, "editingSnapshotById", elevationId]);
    }

    case CLEAR_ELEVATION_SNAPSHOTS:
      // Drop all in-wizard checkpoints when the modal closes so the next session's first Cancel
      // rolls back to the server baseline, not a stale checkpoint from a prior session.
      return state.setIn([model, "editingSnapshotById"], Map<number, ElevationRecord>());

    case UPDATE_ELEVATION_FIELD: {
      const { elevationId, field, value } = action.payload;
      const elevation = state.getIn([model, "byId", elevationId]) as ElevationRecord | undefined;
      if (!elevation) return state;
      return state.setIn([model, "byId", elevationId], elevation.set(field as any, value));
    }

    case ADD_ANNOTATION: {
      const { elevationId, annotation } = action.payload;
      const elevation = state.getIn([model, "byId", elevationId]) as ElevationRecord | undefined;
      if (!elevation) return state;

      const list = elevation.annotations.get("list");
      // `sort_order` is deliberately NOT assigned here: it is the annotation→opening link key,
      // written only by the save-flow merge when an opening is created for the annotation.
      // Pre-assigning 0..n at placement collides with the server-assigned sort_orders on
      // table-created openings, letting the merge absorb (rename/resize) unrelated pre-existing
      // openings. An explicit `sort_order` in the payload still wins via the spread (same as `id`).
      // Assign a within-elevation `id` of `max(existing id) + 1` on placement so the derived label
      // (`annotationLabel` → `${abbr}${id}`) is unique and round-trips. Using `max + 1` (not
      // `list.length + 1`) keeps ids monotonic across mid-list deletes — `length + 1` would reissue
      // an id already held by a surviving higher row (e.g. [1,2,3] → delete 2 → length 2 → next id 3,
      // colliding). Callers that pass an explicit `id` (rehydration) win via the spread.
      const maxId = list.reduce((acc, a) => Math.max(acc, a.id ?? 0), 0);
      const merged = annotationFromJSON({
        id: maxId + 1,
        ...annotation,
      });
      return state.setIn([model, "byId", elevationId, "annotations", "list"], list.push(merged));
    }

    case MOVE_ANNOTATION: {
      const { elevationId, guid, x, y } = action.payload;
      const elevation = state.getIn([model, "byId", elevationId]) as ElevationRecord | undefined;
      if (!elevation) return state;

      const list = elevation.annotations.get("list");
      const updatedList = list.map((a) => (a.guid === guid ? a.set("x", x).set("y", y) : a));
      if (updatedList.equals(list)) return state;
      return state.setIn([model, "byId", elevationId, "annotations", "list"], updatedList);
    }

    case REMOVE_ANNOTATION: {
      const { elevationId, guid } = action.payload;
      const elevation = state.getIn([model, "byId", elevationId]) as ElevationRecord | undefined;
      if (!elevation) return state;

      const list = elevation.annotations.get("list");
      const removed = list.find((a) => a.guid === guid);
      if (!removed) return state;
      const remaining = list.filter((a) => a.guid !== guid);
      const removedList = (elevation.removedAnnotations ?? List<AnnotationRecord>()).push(removed);
      // `$hasChanged` here is load-bearing — a `removedAnnotations` entry that carried an
      // `opening_id` is drained into `_destroy` on the matching Window/Door during save (see
      // `mergeElevationsIntoOpenings` in `saveElevations`), so the dirty signal must survive even
      // if the user immediately re-adds an identical-looking annotation. Sticky-after-remove is
      // correct; the toggle-back-to-clean concern that drove dropping `$hasChanged` from
      // UPDATE_ANNOTATIONS doesn't apply to removals.
      const next = elevation
        .set("annotations", elevation.annotations.set("list", remaining))
        .set("removedAnnotations", removedList)
        .set("$hasChanged", true);
      return state.setIn([model, "byId", elevationId], next);
    }

    case SET_MEASURING_METADATA: {
      const { elevationId, metadata } = action.payload;
      const elevation = state.getIn([model, "byId", elevationId]) as ElevationRecord | undefined;
      if (!elevation) return state;
      const nextAnnotations = elevation.annotations.set("measuring", metadata);
      // No `$hasChanged` set: `elevationsChanged` already detects nested Map changes via deep
      // `.equals()` against `lastSavedById`, and the sticky flag would block clean revert.
      return state.setIn([model, "byId", elevationId], elevation.set("annotations", nextAnnotations));
    }

    case UPDATE_ANNOTATIONS: {
      const { targets, field, value } = action.payload.event;
      const byElevation = groupTargetsByElevation(targets);

      byElevation.forEach((guids, elevationId) => {
        const elevation = state.getIn([model, "byId", elevationId]) as ElevationRecord | undefined;
        if (!elevation) return;

        const list = elevation.annotations.get("list");
        const updatedList = list.map((annotation) =>
          guids.has(annotation.guid) ? annotation.set(field as keyof AnnotationRecord, value) : annotation,
        );

        if (updatedList.equals(list)) return;

        // Don't set `$hasChanged` here: `recordChanged` (used by `elevationsChanged`) already
        // detects updates via deep `.equals()` against `lastSavedById`, and a sticky `$hasChanged`
        // never gets cleared if the user edits a value back to its last-saved state — that would
        // keep the modal dirty even when the data again matches the saved record.
        const updatedElevation = elevation.set("annotations", elevation.annotations.set("list", updatedList));
        state = state.setIn([model, "byId", elevationId], updatedElevation);
      });

      return state;
    }

    default:
      return state;
  }
};

const groupTargetsByElevation = (targets: AnnotationTarget[]): Map<number, Set<string>> => {
  let result = Map<number, Set<string>>();
  for (const { elevationId, guid } of targets) {
    const existing = result.get(elevationId) ?? new Set<string>();
    existing.add(guid);
    result = result.set(elevationId, existing);
  }
  return result;
};
